import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import LoadingButton from "../components/LoadingButton";
import LanguageSelector from "../components/LanguageSelector";
import countriesData from "../data/countries.json";
import {
  extractInternationalNumber,
  splitCountryAndNumber,
} from "../utils/phoneUtils";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import {
  Settings as SettingsIcon,
  Phone,
  Plus,
  Trash2,
  CreditCard,
  Save,
  Globe,
  User,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Mail,
  Copy,
  Send,
} from "lucide-react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export default function Settings() {
  const { user, setUser } = useAuth();
  const { t, i18n } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [error, setError] = useState(null);
  const [phones, setPhones] = useState([]);
  const [phoneConflict, setPhoneConflict] = useState(null); // { phone, existing_company_id }
  const [transferCode, setTransferCode] = useState("");
  const [transferStatus, setTransferStatus] = useState(null);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [language, setLanguage] = useState(
    user?.language || i18n.language || "en"
  );

  // Update language when user changes
  useEffect(() => {
    if (user?.language) {
      setLanguage(user.language);
      i18n.changeLanguage(user.language);
    }
  }, [user?.language, i18n]);

  // Parse user.phones (full numbers) into country + local number
  useEffect(() => {
    const rawPhones = user?.phones || [];
    let parsedPhones = rawPhones;
    // Handle stringified arrays coming from different backends
    if (typeof rawPhones === "string") {
      try {
        parsedPhones = JSON.parse(rawPhones);
      } catch (err) {
        // If parsing fails, try to extract phone-looking substrings
        const matched = (rawPhones.match(/\+\d{6,15}/g) || []).map((m) => m);
        parsedPhones = matched.length ? matched : [rawPhones];
      }
    }
    const fromUserPhones = (
      Array.isArray(parsedPhones) ? parsedPhones : [parsedPhones]
    ).map((p) => {
      if (!p) return { country: "ES", number: "" };
      if (
        typeof p === "object" &&
        Object.prototype.hasOwnProperty.call(p, "S")
      ) {
        p = p.S;
      }
      if (typeof p === "object") {
        return {
          country: p.country || user?.country || "ES",
          number: p.number || "",
        };
      }
      // Normalize number using helper
      const extracted = extractInternationalNumber(p);
      if (extracted) {
        const { country, number } = splitCountryAndNumber(
          extracted,
          countriesData,
          user?.country || "ES"
        );
        return { country, number };
      }
      return {
        country: user?.country || "ES",
        number: String(p).replace(/[^0-9]/g, ""),
      };
    });
    const finalPhones = fromUserPhones.length
      ? fromUserPhones
      : [{ country: user?.country || "ES", number: "" }];
    setPhones(finalPhones);
  }, [user]);

  const addPhone = () =>
    setPhones([...phones, { country: user?.country || "ES", number: "" }]);

  const updatePhone = (idx, key, value) =>
    setPhones(phones.map((p, i) => (i === idx ? { ...p, [key]: value } : p)));

  const removePhone = (idx) => setPhones(phones.filter((_, i) => i !== idx));

  const loadSubscriptionData = useCallback(async () => {
    if (!user) return;
    try {
      const subscription = await api.getSubscription().catch(() => null);
      setSubscriptionData(subscription);
    } catch (err) {
      console.error("Error loading subscription data:", err);
    }
  }, [user]);

  useEffect(() => {
    loadSubscriptionData();
  }, [loadSubscriptionData]);

  const handleManageSubscription = async () => {
    try {
      setError(null);
      const { portalUrl } = await api.getCustomerPortalUrl();
      window.location.href = portalUrl;
    } catch (err) {
      console.error("Error opening customer portal:", err);
      setError(t("settings.errorPortal", "Error opening billing portal"));
    }
  };

  const save = async () => {
    const payloadPhones = phones
      .map((p) => {
        if (!p) return null;
        if (
          typeof p === "object" &&
          Object.prototype.hasOwnProperty.call(p, "S")
        ) {
          return p.S;
        }
        const countryObj = countriesData.find((c) => c.code === p.country);
        const dial = countryObj?.dial_code || "+34";
        const cleanNumber = String(p.number || "").replace(/[^0-9]/g, "");
        if (!cleanNumber) return null;
        return `${dial}${cleanNumber}`;
      })
      .filter(Boolean);
    setSaving(true);
    try {
      setPhoneConflict(null);
      setTransferStatus(null);
      const response = await api.updateUser({
        phones: payloadPhones,
        language: language,
      });
      const updatedUser = response.user || response;
      setUser(updatedUser);
      // Update i18n language
      i18n.changeLanguage(language);
      // Optional: Show success toast instead of alert, but alert for now matches logic
      // alert(t("settings.saved"));
    } catch (err) {
      const apiData = err?.response?.data;
      if (
        err?.response?.status === 409 &&
        apiData?.error_code === "PHONE_IN_USE"
      ) {
        setError(null);
        setPhoneConflict({
          phone: apiData.phone,
          existing_company_id: apiData.existing_company_id,
        });
        setTransferCode("");
        setSendingCode(true);
        try {
          await api.requestPhoneTransferCode({ phone: apiData.phone });
          setTransferStatus(
            t(
              "settings.codeSent",
              "Code sent to that number's WhatsApp and to its current owner's email. Enter it below to confirm."
            )
          );
        } catch (e) {
          setTransferStatus(
            t(
              "settings.codeSendError",
              "Could not send the code. Please try again."
            )
          );
        } finally {
          setSendingCode(false);
        }
      } else {
        setError(t("settings.errorSave", "Error saving settings"));
      }
    } finally {
      setSaving(false);
    }
  };

  const requestTransferCode = async () => {
    if (!phoneConflict?.phone) return;
    setSendingCode(true);
    setTransferStatus(null);
    try {
      await api.requestPhoneTransferCode({ phone: phoneConflict.phone });
      setTransferStatus(
        t(
          "settings.codeSent",
          "Code sent to that number's WhatsApp and to its current owner's email. Enter it below to confirm."
        )
      );
    } catch (err) {
      setTransferStatus(
        t(
          "settings.codeSendError",
          "Could not send the code. Please try again."
        )
      );
    } finally {
      setSendingCode(false);
    }
  };

  const verifyTransferCode = async () => {
    if (!phoneConflict?.phone) return;
    const code = String(transferCode || "").trim();
    if (!/^\d{6}$/.test(code)) {
      setTransferStatus(t("settings.codeInvalid", "Enter a 6-digit code."));
      return;
    }
    setVerifyingCode(true);
    setTransferStatus(null);
    try {
      await api.verifyPhoneTransferCode({ phone: phoneConflict.phone, code });
      setTransferStatus(t("settings.transferOk", "Verified. Retrying save..."));
      setTransferCode("");
      setPhoneConflict(null);
      await save();
    } catch (err) {
      const apiData = err?.response?.data;
      const msg =
        i18n.language === "es"
          ? apiData?.message || "Código incorrecto o expirado."
          : apiData?.message_en ||
            apiData?.message ||
            "Incorrect or expired code.";
      setTransferStatus(msg);
    } finally {
      setVerifyingCode(false);
    }
  };

  const currentPlan =
    subscriptionData?.subscription_plan || user?.subscription_plan || "free";
  const hasPaidPlan = currentPlan && currentPlan !== "free";
  const isCanceling = subscriptionData?.subscription_status === "canceling";
  const cancelEffectiveAt = subscriptionData?.next_billing_date || null;
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat(i18n.language === "es" ? "es-ES" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(d);
  };

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-6 max-w-4xl mx-auto space-y-8 min-h-screen pb-20"
    >
      <div className="flex items-center gap-4 mb-4">
        <div className="p-3 bg-white rounded-xl shadow-sm border border-gray-100 hidden sm:block">
          <SettingsIcon className="w-6 h-6 text-gray-700" />
        </div>
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            {t("settings.title") || "Settings"}
          </h1>
          <p className="text-gray-500">
            {t(
              "settings.description",
              "Manage your account preferences and connected devices."
            )}
          </p>
        </div>
      </div>

      {/* Aviso desactivado: con WhatsApp opcional, `phone_required` lo tiene
          toda cuenta sin teléfono y el texto («te has quedado sin números»)
          era falso para quien nunca añadió uno. */}
      {false && user?.phone_required && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-xl flex items-start gap-2">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">
              {t(
                "settings.phoneRequiredTitle",
                "Añade un número para seguir usando WhatsApp"
              )}
            </p>
            <p className="text-sm text-yellow-800/80">
              {t(
                "settings.phoneRequiredBody",
                "Tu organización se ha quedado sin números activos. Añade un número para reactivar el servicio."
              )}
            </p>
          </div>
        </div>
      )}

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
            <button
              onClick={() => setError(null)}
              className="font-bold hover:bg-red-100 p-1 rounded"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {phoneConflict && (
        <div className="bg-primary-50/50 border border-primary-100 text-primary-900 px-4 py-4 rounded-xl">
          <div className="font-bold mb-1">
            {t("settings.phoneConflictTitle", "Phone already in use")}
          </div>
          <div className="text-sm">
            {transferStatus ||
              t(
                "settings.phoneConflictHelp",
                "Verify the phone with the 6-digit code to transfer it to your organization."
              )}
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={requestTransferCode}
              disabled={sendingCode}
              className={cn(
                "px-4 py-2 rounded-xl font-bold border",
                sendingCode
                  ? "bg-gray-100 text-gray-500 border-gray-200"
                  : "bg-white hover:bg-primary-50 border-primary-200"
              )}
            >
              {sendingCode
                ? t("settings.sending", "Sending...")
                : t("settings.sendCode", "Send code")}
            </button>
            <input
              value={transferCode}
              onChange={(e) => setTransferCode(e.target.value)}
              inputMode="numeric"
              pattern="\\d{6}"
              maxLength={6}
              placeholder={t("settings.codePlaceholder", "6-digit code")}
              className="flex-1 px-4 py-2 rounded-xl border border-primary-200 bg-white focus:ring-2 focus:ring-primary-200 outline-none"
            />
            <button
              type="button"
              onClick={verifyTransferCode}
              disabled={verifyingCode}
              className={cn(
                "px-4 py-2 rounded-xl font-bold",
                verifyingCode
                  ? "bg-gray-100 text-gray-500"
                  : "bg-gray-900 hover:bg-black text-white"
              )}
            >
              {verifyingCode
                ? t("settings.verifying", "Verifying...")
                : t("settings.verify", "Verify")}
            </button>
          </div>
          <div className="mt-2 text-xs text-gray-700">
            {t("settings.conflictPhone", "Phone")}: {phoneConflict.phone}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Phones & General */}
        <div className="lg:col-span-2 space-y-8">
          {/* Documentos por correo (email_inbound): la dirección de la cuenta. */}
          {user?.inbox_address && <InboxCard address={user.inbox_address} t={t} />}
          {user?.telegram_link && <TelegramCard link={user.telegram_link} chats={user.telegram_chats} t={t} />}

          {/* Connected Phones Section */}
          <section className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden relative">
            <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 text-green-600 rounded-lg">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">
                    {t("settings.connected_numbers_title", "Connected Numbers")}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {t(
                      "settings.connected_numbers_subtitle",
                      "For WhatsApp processing"
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="text-sm text-gray-600 bg-blue-50 p-4 rounded-xl border border-blue-100 mb-6">
                {t(
                  "settings.phone_numbers_help",
                  "These numbers will be authorized to interact with your scanners via WhatsApp."
                )}
              </div>

              <AnimatePresence initial={false}>
                {phones.map((p, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="group flex flex-col sm:flex-row gap-3 items-start sm:items-center bg-gray-50/50 p-2 rounded-2xl border border-gray-100 hover:border-primary-200 hover:shadow-sm transition-all"
                  >
                    {/* Country Select */}
                    <div className="relative w-full sm:w-48 min-w-[160px]">
                      <select
                        className="w-full appearance-none bg-white border border-gray-200 text-gray-700 rounded-xl pl-10 pr-8 py-3 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all cursor-pointer"
                        value={p.country}
                        onChange={(e) =>
                          updatePhone(i, "country", e.target.value)
                        }
                      >
                        {countriesData.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.name} ({c.dial_code})
                          </option>
                        ))}
                      </select>
                      <Globe className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>

                    {/* Number Input */}
                    <div className="flex-1 w-full relative">
                      <div className="absolute left-0 top-0 bottom-0 px-2 bg-gray-100 border border-gray-200 border-r-0 rounded-l-xl flex items-center text-gray-500 text-sm font-medium">
                        {countriesData.find((c) => c.code === p.country)
                          ?.dial_code || "+34"}
                      </div>
                      <input
                        value={p.number}
                        onChange={(e) =>
                          updatePhone(i, "number", e.target.value)
                        }
                        placeholder="612345678"
                        className="w-full bg-white border border-gray-200 text-gray-900 rounded-xl rounded-l-none pl-12 pr-10 py-3 relative z-10 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                      />
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => removePhone(i)}
                      className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors self-end sm:self-auto"
                      title={t("settings.remove", "Remove")}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>

              {phones.length <
              (user?.max_team_members === -1
                ? Infinity
                : user?.max_team_members || 1) ? (
                <button
                  onClick={addPhone}
                  className="w-full py-3 border-2 border-dashed border-gray-200 hover:border-primary-300 hover:bg-primary-50 text-gray-500 hover:text-primary-600 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all group"
                >
                  <div className="bg-gray-100 group-hover:bg-primary-100 p-1 rounded-full transition-colors">
                    <Plus className="w-4 h-4" />
                  </div>
                  {t("settings.add_phone") || "Add Another Number"}
                </button>
              ) : (
                <div className="w-full py-3 border-2 border-dashed border-amber-100 bg-amber-50/30 text-amber-600 rounded-2xl font-medium flex flex-col items-center justify-center gap-1 transition-all">
                  <p className="text-sm">
                    {t("settings.phone_limit_reached", {
                      limit:
                        user?.max_team_members === -1 ||
                        user?.max_team_members === null
                          ? "∞"
                          : user?.max_team_members || 1,
                    }) ||
                      `Has alcanzado el límite de ${
                        user?.max_team_members === -1 ||
                        user?.max_team_members === null
                          ? "∞"
                          : user?.max_team_members || 1
                      } números para tu plan.`}
                  </p>
                  <Link
                    to="/pricing"
                    className="text-[10px] text-primary-600 hover:text-primary-700 font-bold underline transition-colors"
                  >
                    {t(
                      "settings.upgrade_for_more",
                      "Upgrade your plan for unlimited members"
                    )}
                  </Link>
                </div>
              )}
            </div>

            <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end">
              <LoadingButton
                loading={saving}
                onClick={save}
                className="flex items-center gap-2 bg-gray-900 hover:bg-black text-white px-8 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                <Save className="w-4 h-4" />{" "}
                {t("settings.save") || "Save Changes"}
              </LoadingButton>
            </div>
          </section>
        </div>

        {/* Right Column: Profile & Subscription */}
        <div className="space-y-8">
          {/* Profile Card */}
          <section className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-24 h-24 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mb-4 border-4 border-white shadow-sm ring-1 ring-gray-100">
                <User className="w-10 h-10 text-primary-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 break-all">
                {user?.email}
              </h3>
            </div>
          </section>

          {/* Language Selection */}
          <section className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-primary-50 text-primary-600 rounded-lg">
                <Globe className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">
                  {t("settings.language.title", "Application Language")}
                </h3>
                <p className="text-xs text-gray-500">
                  {t(
                    "settings.language.subtitle",
                    "Choose your preferred language"
                  )}
                </p>
              </div>
            </div>
            <LanguageSelector value={language} onChange={setLanguage} />
            <div className="mt-4 pt-4 border-t border-gray-100">
              <LoadingButton
                loading={saving}
                onClick={save}
                className="w-full bg-gray-900 hover:bg-black text-white px-4 py-2 rounded-xl font-bold text-sm"
              >
                {t("common.save", "Save")}
              </LoadingButton>
            </div>
          </section>

          {/* Subscription Card */}
          <section
            className={cn(
              "rounded-3xl shadow-lg border p-6 relative overflow-hidden",
              hasPaidPlan
                ? "bg-gradient-to-br from-gray-900 to-gray-800 text-white border-gray-700"
                : "bg-white text-gray-900 border-gray-100"
            )}
          >
            {/* Background Flair for Paid Plan */}
            {hasPaidPlan && (
              <div className="absolute top-0 right-0 p-24 bg-primary-500/20 rounded-full blur-3xl -mr-12 -mt-12 pointer-events-none" />
            )}

            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div
                  className={cn(
                    "p-2 rounded-lg",
                    hasPaidPlan
                      ? "bg-white/10"
                      : "bg-primary-50 text-primary-600"
                  )}
                >
                  <CreditCard className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-lg">
                  {t("settings.subscription.title", "Subscription")}
                </h3>
              </div>

              <div className="mb-6">
                <div
                  className={cn(
                    "text-xs font-bold uppercase tracking-wider mb-1",
                    hasPaidPlan ? "text-gray-400" : "text-gray-500"
                  )}
                >
                  {t("tokens.yourPlan", "Current Plan")}
                </div>
                <div className="text-3xl font-extrabold capitalize">
                  {currentPlan}
                </div>
                {hasPaidPlan && (
                  <div className="mt-2 text-sm font-medium">
                    {isCanceling ? (
                      <div className="flex items-center gap-2 text-amber-300">
                        <AlertCircle className="w-4 h-4" />
                        <span>
                          {t(
                            "settings.subscription.canceling",
                            "Canceled. Active until"
                          )}{" "}
                          {formatDate(cancelEffectiveAt) || "—"}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle className="w-4 h-4" />{" "}
                        {t("settings.subscription.active", "Active")}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {hasPaidPlan ? (
                <button
                  onClick={handleManageSubscription}
                  className="w-full bg-white text-gray-900 hover:bg-gray-100 py-3 rounded-xl font-bold text-sm shadow-md transition-colors flex items-center justify-center gap-2"
                >
                  {t("settings.subscription.manageButton", "Manage Billing")}{" "}
                  <ExternalLink className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => (window.location.href = "/pricing")}
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-xl font-bold text-sm shadow-md shadow-primary-500/25 transition-colors"
                >
                  {t("tokens.upgradePlan", "Upgrade Plan")}
                </button>
              )}
            </div>
          </section>

          {hasPaidPlan && (
            <div className="text-center">
              <button
                onClick={handleManageSubscription}
                className="text-xs text-gray-400 hover:text-gray-600 underline"
              >
                {t(
                  "settings.subscription.invoices_help",
                  "Need invoice history?"
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </motion.main>
  );
}

/**
 * Dirección para recibir documentos por correo: lo que llegue ahí (adjuntos PDF
 * o imagen) se procesa con la plantilla adecuada y aparece en el historial.
 */
function InboxCard({ address, t }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* el navegador no deja copiar: la dirección sigue visible */
    }
  };
  return (
    <section className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6" data-testid="inbox-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-sky-100 text-sky-600 rounded-lg">
          <Mail className="w-5 h-5" />
        </div>
        <h2 className="text-lg font-bold text-gray-900">{t("settings.inbox.title", "Receive documents by email")}</h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        {t("settings.inbox.text", "Forward invoices, receipts or statements to this address. Every PDF or image attached is processed with the right template and shows up in your history.")}
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <code className="flex-1 min-w-0 break-all rounded-xl bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-900">{address}</code>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
        >
          <Copy className="w-4 h-4" />
          {copied ? t("settings.inbox.copied", "Copied") : t("settings.inbox.copy", "Copy")}
        </button>
      </div>
      <p className="mt-3 text-xs text-gray-500">
        {t("settings.inbox.hint", "Tip: give it to your suppliers or set up a forwarding rule in your inbox. Each page uses 1 credit.")}
      </p>
    </section>
  );
}

/**
 * Telegram (@WhappScanBot): el botón abre el bot con el código de la cuenta;
 * al pulsar «Iniciar», ese chat queda conectado. Varios chats por cuenta.
 */
function TelegramCard({ link, chats, t }) {
  const connected = Object.values(chats || {});
  return (
    <section className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6" data-testid="telegram-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
          <Send className="w-5 h-5" />
        </div>
        <h2 className="text-lg font-bold text-gray-900">{t("settings.telegram.title", "Send documents by Telegram")}</h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        {t("settings.telegram.text", "Connect Telegram and send photos or PDFs to @WhappScanBot: you get the data back in the chat and the document appears in your history.")}
      </p>
      {connected.length > 0 && (
        <p className="mb-3 text-sm text-gray-700">
          <span className="font-semibold">{t("settings.telegram.connected", "Connected")}:</span> {connected.join(", ")}
        </p>
      )}
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-xl bg-[#229ED9] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
      >
        <Send className="w-4 h-4" />
        {connected.length ? t("settings.telegram.connectAnother", "Connect another chat") : t("settings.telegram.connect", "Connect Telegram")}
      </a>
      <p className="mt-3 text-xs text-gray-500">
        {t("settings.telegram.hint", "Opens Telegram: press «Start». Anyone with this link can send documents to your account, so only share it with your team.")}
      </p>
    </section>
  );
}
